import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Gift, Sparkles, Star } from "lucide-react";

export type PrizeDto = {
  PrizeId: number;
  code: string;
  name: string;
  type: string;
  valueJson: string;
};

type WheelSegment = {
  key: string;
  labelKey: string;
};

type Props = {
  segments?: WheelSegment[];
  onSpin: () => Promise<PrizeDto>;
  mapPrizeToKey?: (p: PrizeDto) => string;
  onDone?: (p: PrizeDto) => void;
};

const DEFAULT_SEGMENTS: WheelSegment[] = [
  { key: "CP_PCT_10", labelKey: "voucherGame.wheel.segment.pct10" },
  { key: "CP_FIX_50K", labelKey: "voucherGame.wheel.segment.fix50k" },
  { key: "FS_30K", labelKey: "voucherGame.wheel.segment.freeship" },
  { key: "PT_100", labelKey: "voucherGame.wheel.segment.plus100" },
  { key: "OTHER", labelKey: "voucherGame.wheel.segment.lucky" },
  { key: "CP_PCT_10_2", labelKey: "voucherGame.wheel.segment.pct10" },
  { key: "CP_FIX_50K_2", labelKey: "voucherGame.wheel.segment.fix50k" },
  { key: "FS_30K_2", labelKey: "voucherGame.wheel.segment.freeship" },
  { key: "PT_100_2", labelKey: "voucherGame.wheel.segment.plus100" },
  { key: "CP_PCT_10_3", labelKey: "voucherGame.wheel.segment.pct10" },
  { key: "FS_30K_3", labelKey: "voucherGame.wheel.segment.freeship" },
  { key: "CP_FIX_50K_3", labelKey: "voucherGame.wheel.segment.fix50k" },
];

function cn(...x: Array<string | false | undefined | null>) {
  return x.filter(Boolean).join(" ");
}

function defaultMapPrizeToKey(p: PrizeDto) {
  return p.code || "OTHER";
}

export default function WheelGame({
  segments = DEFAULT_SEGMENTS,
  onSpin,
  mapPrizeToKey = defaultMapPrizeToKey,
  onDone,
}: Props) {
  const { t } = useTranslation();

  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState<PrizeDto | null>(null);

  const wheelRef = useRef<HTMLDivElement | null>(null);
  const lastRotationRef = useRef(0);

  const segmentAngle = useMemo(() => 360 / segments.length, [segments.length]);
  const skew = useMemo(() => 90 - segmentAngle, [segmentAngle]);

  const spinToIndex = (stopIndex: number, prize: PrizeDto) => {
    const el = wheelRef.current;
    if (!el) return;

    const extraTurns = 6;
    const target =
      extraTurns * 360 + (360 - stopIndex * segmentAngle - segmentAngle / 2);

    const nextRotation = lastRotationRef.current + target;
    lastRotationRef.current = nextRotation;

    el.style.transition = "transform 4.2s cubic-bezier(0.15, 0.85, 0.2, 1)";
    el.style.transform = `rotate(${nextRotation}deg)`;

    window.setTimeout(() => {
      setResult(prize);
      setSpinning(false);
      onDone?.(prize);
    }, 4300);
  };

  const handleSpin = async () => {
    if (spinning) return;

    setSpinning(true);
    setResult(null);

    try {
      const prize = await onSpin();
      const prizeKey = mapPrizeToKey(prize);

      let idx = segments.findIndex((s) => s.key === prizeKey);

      if (idx === -1) {
        const name = (prize.name || "").toLowerCase();
        idx = segments.findIndex((s) =>
          t(s.labelKey).toLowerCase().includes(name),
        );
      }

      if (idx === -1) idx = Math.floor(Math.random() * segments.length);

      spinToIndex(idx, prize);
    } catch (e) {
      setSpinning(false);
      throw e;
    }
  };

  return (
    <div className="text-center text-brand-brown">
      {/* HEAD */}
      <div className="mb-5">
        <div className="inline-flex items-center gap-3 rounded-full border border-black/10 bg-white/58 px-4 py-2 backdrop-blur-sm">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-black/[0.03]">
            <Sparkles className="h-4 w-4 text-brand-gold" />
          </span>
          <span className="text-[11px] font-medium tracking-[0.1em] text-black/62">
            {t("voucherGame.wheel.kicker")}
          </span>
        </div>

        <h3 className="mt-5 text-[22px] font-medium leading-[1.02] tracking-[0.01em] text-brand-brown sm:text-[26px]">
          {t("voucherGame.wheel.title")}
        </h3>

        <p className="mx-auto mt-3 max-w-md text-sm leading-7 text-black/54">
          Quay để mở phần thưởng ngẫu nhiên từ voucher của bạn.
        </p>
      </div>

      {/* POINTER */}
      <div className="mb-3 flex justify-center">
        <div
          className="h-0 w-0 drop-shadow-sm
                     border-l-[11px] border-l-transparent
                     border-r-[11px] border-r-transparent
                     border-b-[18px] border-b-black/75"
          aria-hidden="true"
        />
      </div>

      {/* WHEEL */}
      <div className="mx-auto w-[300px] h-[300px] sm:w-[340px] sm:h-[340px]">
        <div
          className="relative h-full w-full overflow-hidden rounded-full border-[10px] border-black/75
                     bg-white/62 shadow-[0_24px_70px_rgba(0,0,0,0.12)]"
        >
          <div
            ref={wheelRef}
            className="absolute inset-0 rounded-full"
            style={{ transform: "rotate(0deg)" }}
          >
            {segments.map((seg, i) => {
              const isEven = i % 2 === 0;

              return (
                <div
                  key={`${seg.key}-${i}`}
                  className="absolute left-1/2 top-1/2 h-1/2 w-1/2 border border-black/8"
                  style={{
                    transformOrigin: "0% 0%",
                    transform: `rotate(${i * segmentAngle}deg) skewY(${skew}deg)`,
                    background: isEven
                      ? "rgba(255,255,255,0.76)"
                      : "rgba(255,255,255,0.46)",
                  }}
                >
                  <div
                    className="absolute left-3 top-3 w-[150px] text-left"
                    style={{
                      transformOrigin: "0% 0%",
                      transform: `skewY(-${skew}deg) rotate(${segmentAngle / 2}deg)`,
                    }}
                  >
                    <p className="text-[10px] sm:text-[11px] font-semibold tracking-[0.08em] text-black/72">
                      {t(seg.labelKey)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* CENTER */}
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full border border-black/12 bg-white/78 shadow-sm backdrop-blur-sm">
              <div className="flex h-10 w-10 items-center justify-center rounded-full border border-black/10 bg-black/[0.03]">
                <Star className="h-4 w-4 text-brand-gold" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ACTION */}
      <div className="mt-6 flex justify-center">
        <button
          type="button"
          onClick={handleSpin}
          disabled={spinning}
          aria-label={t("voucherGame.wheel.spinAria")}
          className={cn(
            "inline-flex h-12 min-w-[260px] items-center justify-center gap-3 rounded-full px-6",
            "text-[11px] font-medium tracking-[0.08em] transition-all duration-300",
            spinning
              ? "cursor-not-allowed bg-black/20 text-black/40"
              : "bg-brand-brown text-brand-ivory hover:opacity-95",
          )}
        >
          {spinning ? (
            <>
              <span className="h-4 w-4 rounded-full border-2 border-brand-ivory/35 border-t-brand-ivory animate-spin" />
              {t("voucherGame.wheel.spinning")}
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4" />
              {t("voucherGame.wheel.spin")}
            </>
          )}
        </button>
      </div>

      {/* RESULT */}
      {result && (
        <div className="mt-6">
          <div className="mx-auto max-w-md rounded-[24px] border border-black/10 bg-white/62 p-5 backdrop-blur-sm">
            <div className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-brand-ivory/75 px-4 py-2 text-[11px] font-medium tracking-[0.08em] text-black/62">
              <Gift className="h-4 w-4 text-brand-gold" />
              {t("voucherGame.wheel.rewardKicker")}
            </div>

            <p className="mt-4 text-sm leading-7 text-black/56">
              {t("voucherGame.wheel.youWin")}
            </p>

            <p className="mt-3 text-[18px] font-medium leading-8 tracking-[0.01em] text-brand-brown">
              {result.name}
            </p>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="rounded-[18px] border border-black/10 bg-white/55 px-4 py-3">
                <p className="text-[11px] font-medium tracking-[0.08em] text-black/44">
                  Type
                </p>
                <p className="mt-2 text-sm text-black/72">{result.type}</p>
              </div>

              <div className="rounded-[18px] border border-black/10 bg-white/55 px-4 py-3">
                <p className="text-[11px] font-medium tracking-[0.08em] text-black/44">
                  Code
                </p>
                <p className="mt-2 text-sm font-medium text-black/78">
                  {result.code}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}